import {Fragment, useEffect, useState} from 'react';
import FuzzySet from "fuzzyset.js";
import {Attribute} from "@/components/Attribute/Attribute.jsx";
import {App as AntdApp, Tooltip, Typography} from "antd";
import api from "@/api.js";
import Auth from "@/Auth.js";

import styles from "./ItemRow.module.css";
import {Link} from "react-router-dom";
import {SearchTableColumns} from "@/variants/index.js";

export const ItemRow = ({
	index,
	item,
	showShortDescr,
	shouldShow,
	showCommonality,
	manipulateAll,
	showAll,
	onRequestAttr,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [image, setImage] = useState(null);
	const [matchedRows, setMatchedRows] = useState(item.matched || []);

	const {message} = AntdApp.useApp();

	const matchCount = item.matched ? item.matched.length - 1 : 0;

	const attrs = item.attributes.map((v, i) => <Attribute key={i} attr={v} onRequestAttr={onRequestAttr} />);

	function renderClassLibrary() {
		if (item.cl_name === "NA") {
			return <span>{item.cl_name}</span>;
		}

		const tooltipContent = `${item.cl_iso} ${item.cl_ref} ${item.cl_type}`;

		return (
			<Tooltip title={tooltipContent} placement="top" mouseEnterDelay={0.3} mouseLeaveDelay={0.15}>
				<span className={styles.itemMaterialLabel}>{item.cl_name}</span>
			</Tooltip>
		);
	}

	function renderOpenIcon() {
		const iconSrc = isOpen ? "/images/up-blue.png" : "/images/down-blue.png";

		return (
			<div>
				<img src={iconSrc} alt="toggle-icon" />
				<Typography.Link component="span">{matchCount}</Typography.Link>
			</div>
		);
	}

	function getDataLink(operator, material, url) {
		const handleClick = () => {
			sessionStorage.setItem("item_url", url);
			localStorage.setItem("item_url", url);
		};
		return (
			<Link
				target="_blank"
				rel="noopener noreferrer"
				onClick={handleClick}
				to={`/data?operator=${encodeURIComponent(operator)}&material=${material}`}>
				<img src="/images/menu-full.png" alt="data-link" />
			</Link>
		);
	}

	async function getImage() {
		try {
			const config = {...Auth.headers(), responseType: "blob"};
			const {operator, material, part_number} = item;
			const url = `/image?operator=${encodeURIComponent(operator)}&material=${encodeURIComponent(material)}&part_number=${encodeURIComponent(part_number)}`;

			const response = await api.get(url, config);

			const objectUrl = URL.createObjectURL(response.data);
			setImage(objectUrl);
		} catch (error) {
			console.error(error);
		}
	}

	function handleShow(shouldChange) {
		if (shouldChange !== undefined) {
			if (shouldChange !== isOpen) {
				setIsOpen(shouldChange);
			}
			return;
		}

		if (!isOpen && matchedRows.length === 0) {
			sendRequest();
		} else {
			if (matchedRows.length === 0) {
				message.warning("No matches found!", 1.5);
			} else if (matchedRows.length === 1) {
				message.warning(SearchConfig.includeOutOfStockWarning, 1.5);
			} else {
				setIsOpen(!isOpen);
			}
		}
	}

	function highlightMultiple(text, words) {
		if (!text) return "";
		const cleanWords = [...new Set(words.filter(w => w && w.trim() !== ""))];
		if (cleanWords.length === 0) return text;

		const pattern = cleanWords.map(w => w.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
		const regex = new RegExp(`(${pattern})`, "gi");

		return text
			.split(regex)
			.map((part, i) =>
				cleanWords.some(w => w.toLowerCase() === part.toLowerCase()) ? <strong key={i}>{part}</strong> : part,
			);
	}

	async function sendRequest() {
		try {
			const url = `/search/report?operator=${item.operator}&material=${item.material}&showAll=${showAll}`;
			const response = await api.get(url);
			const data = response.data;

			setMatchedRows(data.matched);

			if (!data || !data.matched || data.matched.length === 0) {
				message.warning("No matches found!", 1.5);
			} else if (data.matched.length === 1) {
				message.warning(SearchConfig.includeOutOfStockWarning, 1.5);
			} else {
				setIsOpen(true);
			}
		} catch (error) {
			console.error(error);
			setMatchedRows(null);
		}
	}

	function generatorMatched(matchedItem) {
		const f = FuzzySet(matchedItem.description?.split(",") || []);

		const getBestMatch = val => {
			const res = f.get(val.trim());
			return res ? res[0][1] : "";
		};

		const pn_original = getBestMatch(matchedItem.part_number_original);
		const pn_matched = getBestMatch(matchedItem.part_number_matched);

		const descr = highlightMultiple(matchedItem.description, [pn_original, pn_matched]);

		return (
			<tr className={styles.rowMatched}>
				<td className={styles.colIndex} colSpan="1" />

				{shouldShow.includes("source") && <td className={styles.colSource}>{item.source}</td>}

				{shouldShow.includes("material") && (
					<td className={styles.colMaterial}>
						<span className={styles.itemMaterialLabel}>
							{matchedItem.operator} {matchedItem.material}
						</span>
					</td>
				)}

				{shouldShow.includes("cl_name") && <td className={styles.colClName}>{matchedItem.cl_name}</td>}

				{shouldShow.includes("mat_type") && (
					<td className={styles.colMatType}>
						{matchedItem.mat_type ? matchedItem.mat_type.split("-")[0].trim() : ""}
					</td>
				)}

				{shouldShow.includes("attrs") && <td className={styles.colAttrs}> {descr} </td>}

				{shouldShow.includes("part_number") && (
					<td className={styles.colPartNumber}>
						{SearchTableColumns.renderPartNumber(matchedItem, matchedItem.part_number_original)}
					</td>
				)}

				{shouldShow.includes("manufacturer") && <td />}

				{shouldShow.includes("stock_level") && (
					<td className={styles.colStockLevel}>
						{matchedItem.stock_level} {matchedItem.unit}
					</td>
				)}

				{shouldShow.includes("score") && <td className={styles.colPartNumber} colSpan="1" />}

				<td>{getDataLink(matchedItem.operator, matchedItem.material, item.url)}</td>

				{shouldShow.includes("commonality") && <td />}

				{shouldShow.includes("jv") && (
					<td className={styles.colJV}>{SearchTableColumns.renderJV(matchedItem)}</td>
				)}

				{shouldShow.includes("image") && <td />}
			</tr>
		);
	}

	useEffect(() => {
		if (item.has_image) {
			getImage();
		}

		return () => {
			if (image) URL.revokeObjectURL(image);
		};
	}, []);

	useEffect(() => {
		if (manipulateAll) {
			handleShow(manipulateAll.value);
		}
	}, [manipulateAll]);

	return (
		<>
			<tr key="main" className={item.type === "equipment" ? styles.rowEquipment : ""}>
				<td className={styles.colIndex}>{index}</td>

				{shouldShow.includes("source") && <td className={styles.colSource}>{item.source}</td>}

				{shouldShow.includes("material") && (
					<td className={styles.colMaterial}>
						<Tooltip
							id={item.material}
							title={showShortDescr ? item.short_description : item.raw.split(",").join(", ")}
							placement="top"
							mouseEnterDelay={0.3}
							mouseLeaveDelay={0.15}
							styles={{body: {maxWidth: 350, width: "max-content"}}}>
							<span className={styles.itemMaterialLabel}>
								{item.operator} <br /> {item.material}
							</span>
						</Tooltip>
					</td>
				)}

				{shouldShow.includes("cl_name") && <td className={styles.colClName}>{renderClassLibrary()}</td>}

				{shouldShow.includes("mat_type") && (
					<td className={styles.colMatType}>{item.mat_type ? item.mat_type.split("-")[0].trim() : ""}</td>
				)}

				{shouldShow.includes("attrs") && <td className={styles.colAttrs}>{attrs}</td>}

				{shouldShow.includes("part_number") && (
					<td className={styles.colPartNumber}>
						{SearchTableColumns.renderPartNumber(item, item.part_number)}
					</td>
				)}

				{shouldShow.includes("manufacturer") && (
					<td className={styles.colManufacturer}> {item.manufacturer} </td>
				)}

				{shouldShow.includes("stock_level") && (
					<td className={styles.colStockLevel}>
						{item.type === "equipment" ? (
							`${item.stock_level} ${item.unit}`
						) : (
							<Link
								to={`/data?operator=${encodeURIComponent(item.operator)}&material=${item.material}&tab=stock-on-hand`}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => {
									sessionStorage.setItem("item_url", item.url);
									localStorage.setItem("item_url", item.url);
								}}>
								{item.stock_level} {item.unit}
							</Link>
						)}
					</td>
				)}

				{shouldShow.includes("score") && <td className={styles.colScore}> {item.score.toFixed(2)} </td>}

				<td className={styles.colDataLink}>{getDataLink(item.operator, item.material, item.url)}</td>

				{shouldShow.includes("commonality") && (
					<td className={styles.colCommonality} onClick={() => showCommonality && handleShow()}>
						{item.matched.length > 1 ? renderOpenIcon() : null}
					</td>
				)}

				{shouldShow.includes("jv") && <td className={styles.colJV}>{SearchTableColumns.renderJV(item)}</td>}

				{shouldShow.includes("image") && (
					<td className={styles.colImage}>{image && <img style={{width: "100%"}} src={image} alt="" />}</td>
				)}
			</tr>

			{isOpen &&
				matchedRows &&
				matchedRows.map((element, idx) => (
					<Fragment key={`match-${idx}`} >
						{!(String(item.material) === element.material && item.operator === element.operator) &&
							generatorMatched(element)}
					</Fragment>
				))}
		</>
	);
};
